/**
 * What a reviewer of an AI proposal reads around it (create from files, refine with AI): files read only in part,
 * industries the catalog does not have, partners it does not list yet, and the gaps the AI reported.
 */

import { AlertTriangle, ListChecks } from 'lucide-react';

import type { ProposalFile, ProposalGap } from '@/api/types/extractions';

import './files.css';

export function CutNotice({ files }: { files: ProposalFile[] }) {
  const cut = files.filter((f) => f.cut || f.truncated);
  if (cut.length === 0) return null;
  return (
    <div className="alert alert-warning" role="status">
      <AlertTriangle aria-hidden="true" />
      <div className="a-body">
        Only part of these files was read: {cut.map((f) => f.filename).join(', ')}. The files share 120,000 characters, so
        anything after the cut was not read.
      </div>
    </div>
  );
}

/**
 * `source` names what the AI read ("The files", "The refinement"); `partnersNote` says what happens to the new
 * partner names.
 */
export function TagNotices({ industries, partners, source, partnersNote }: { industries: string[]; partners: string[]; source: string; partnersNote: string }) {
  return (
    <>
      {industries.length > 0 && (
        <p className="alert alert-info" role="status">
          {source} name industries the catalog does not have: {industries.join(', ')}. They were left out; use Suggest an
          industry to propose them.
        </p>
      )}
      {partners.length > 0 && (
        <p className="alert alert-info" role="status">
          {source} name partners the catalog does not list yet: {partners.join(', ')}. {partnersNote}
        </p>
      )}
    </>
  );
}

export function GapsCard({ gaps, id }: { gaps: ProposalGap[]; id: string }) {
  return (
    <section className="card" aria-labelledby={id}>
      <div className="card-body stack-sm">
        <h2 id={id}>
          <ListChecks aria-hidden="true" /> Gaps ({gaps.length})
        </h2>
        {gaps.length === 0 ? (
          <p className="hint">No gaps reported.</p>
        ) : (
          <ul className="extraction-gaps">
            {gaps.map((g, i) => (
              <li key={`${g.field}-${i}`}>
                <b>{g.field}</b>: {g.reason}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
